(function () {
  const CREED_NAMES = { F: 'Faith', I: 'Integrity', S: 'Strength', H: 'Honor' };

  class CreedSelectorManager {
    constructor(wrapperEl) {
      this.wrapper = wrapperEl;
      this.sectionId = wrapperEl.dataset.sectionId;
      this.currentCreed = wrapperEl.dataset.currentCreed;
      this.currentOptions = JSON.parse(wrapperEl.dataset.currentOptions); // e.g. ["Color","Size"]
      this.currentVariants = JSON.parse(wrapperEl.dataset.currentVariants);

      this.select = wrapperEl.querySelector('select');
      this.labelSpan = document.getElementById(`creed-name-${this.sectionId}`);
      this.errorEl = document.getElementById(`creed-error-${this.sectionId}`);

      this.selectedCreed = this.currentCreed;
      // Tracks the variant id Dawn has selected for the current product.
      this.dawnVariantId = null;

      this.select.addEventListener('change', this.onCreedChange.bind(this));

      // Subscribe to Dawn's variant-change pubsub event so we can re-apply the
      // creed override whenever the user changes size / color.
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, this.onDawnVariantChange.bind(this));
      }

      this.injectCreedProperty();
    }

    // ── Line item property ────────────────────────────────────────────────────

    getCreedPropertyInput() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      return form ? form.querySelector('input[name="properties[Creed]"]') : null;
    }

    injectCreedProperty() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return;
      if (form.querySelector('input[name="properties[Creed]"]')) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Creed]';
      input.value = CREED_NAMES[this.currentCreed] || this.currentCreed;
      form.appendChild(input);
    }

    updateCreedProperty(creed) {
      const input = this.getCreedPropertyInput();
      if (input) {
        input.value = CREED_NAMES[creed] || creed;
      }
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    getFormInput() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      return form ? form.querySelector('input[name="id"]') : null;
    }

    buildOptionsMap(variant, optionNames) {
      const map = {};
      optionNames.forEach((name, i) => {
        map[name] = variant[`option${i + 1}`];
      });
      return map;
    }

    findMatchingVariant(targetVariants, targetOptionNames, optionsMap) {
      return targetVariants.find((variant) =>
        targetOptionNames.every((name, i) => optionsMap[name] === variant[`option${i + 1}`])
      );
    }

    getSelectedOptionData() {
      const opt = this.select.options[this.select.selectedIndex];
      return {
        creed: opt.value,
        variants: JSON.parse(opt.dataset.variants),
        options: JSON.parse(opt.dataset.options),
      };
    }

    // ── Core logic ───────────────────────────────────────────────────────────

    /**
     * Given an optionsMap (e.g. { Color: 'Black', Size: 'LG' }) built from
     * the currently active variant on THIS page, find the matching variant in
     * the target creed product and update the form's hidden variant input.
     */
    applyCreedOverride(optionsMap) {
      if (this.selectedCreed === this.currentCreed) {
        // Current product — Dawn already set the correct variant id.
        return;
      }

      const { variants: targetVariants, options: targetOptionNames } = this.getSelectedOptionData();
      const match = this.findMatchingVariant(targetVariants, targetOptionNames, optionsMap);

      if (!match) {
        this.showError(
          'This size/color combination is not available for the selected creed. Please try a different option.'
        );
        return;
      }

      this.hideError();

      const input = this.getFormInput();
      if (input) {
        input.value = match.id;
      }
    }

    // ── Event handlers ───────────────────────────────────────────────────────

    onCreedChange() {
      const opt = this.select.options[this.select.selectedIndex];
      this.selectedCreed = opt.value;
      this.hideError();

      if (this.labelSpan) {
        this.labelSpan.textContent = CREED_NAMES[this.selectedCreed];
      }

      this.updateCreedProperty(this.selectedCreed);

      if (this.selectedCreed === this.currentCreed) {
        // Switching back — restore Dawn's last known variant for this product.
        const input = this.getFormInput();
        if (input && this.dawnVariantId) {
          input.value = this.dawnVariantId;
        }
        return;
      }

      // Find the current variant on this page and match it in the target creed product.
      const input = this.getFormInput();
      if (!input) return;

      const currentVariantId = this.dawnVariantId || parseInt(input.value);
      const currentVariant = this.currentVariants.find((v) => v.id === currentVariantId);
      if (!currentVariant) return;

      const optionsMap = this.buildOptionsMap(currentVariant, this.currentOptions);
      this.applyCreedOverride(optionsMap);
    }

    /**
     * Called after Dawn's product-info.js processes a size/color variant change.
     * By the time this fires, input[name="id"] has been updated to the current
     * product's new variant. We override it with the matching creed product variant.
     */
    onDawnVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId) return;

      const newVariant = data.variant;
      if (!newVariant) return;

      // Always track Dawn's latest selected variant for this product.
      this.dawnVariantId = newVariant.id;

      if (this.selectedCreed === this.currentCreed) return;

      const optionsMap = this.buildOptionsMap(newVariant, this.currentOptions);
      this.applyCreedOverride(optionsMap);
    }

    // ── Error UI ─────────────────────────────────────────────────────────────

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.style.display = '';
      }
    }

    hideError() {
      if (this.errorEl) {
        this.errorEl.style.display = 'none';
        this.errorEl.textContent = '';
      }
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('[data-creed-selector-wrapper]').forEach((el) => {
      if (!el.dataset.creedInitialized) {
        el.dataset.creedInitialized = 'true';
        new CreedSelectorManager(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
